export interface SelectOption {
  value: string;
  label: string;
  score?: number;
}

export const INDUSTRIES: SelectOption[] = [
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'healthcare', label: 'Healthcare & Life Sciences' },
  { value: 'retail', label: 'Retail & E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government & Public Sector' },
  { value: 'nonprofit', label: 'Non-profit' },
  { value: 'consulting', label: 'Consulting & Professional Services' },
  { value: 'media', label: 'Media & Entertainment' },
  { value: 'transportation', label: 'Transportation & Logistics' },
  { value: 'energy', label: 'Energy & Utilities' },
  { value: 'real-estate', label: 'Real Estate' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'aerospace', label: 'Aerospace & Defense' },
  { value: 'agriculture', label: 'Agriculture & Food' },
  { value: 'telecommunications', label: 'Telecommunications' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'pharmaceutical', label: 'Pharmaceutical' },
  { value: 'other', label: 'Other' }
];

export const COMPANY_SIZES: SelectOption[] = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-1000', label: '201-1000 employees' },
  { value: '1001-5000', label: '1001-5000 employees' },
  { value: '5000+', label: '5000+ employees' }
];

// The "New Scan" business-context survey questions/options moved to the backend as the single
// source of truth (see api/apps/client/scans/services/scan.service.ts getSurveyQuestions,
// fetched via ScanAPI.getSurveyQuestions in NewScan.tsx) — this hardcoded copy had drifted out
// of sync with the backend's own definitions and has been removed.

export interface SeverityLevel {
  id: string;
  name: string;
  color: string;
}

export const SEVERITY_LEVELS: SeverityLevel[] = [
  { id: 'critical', name: 'Critical', color: 'bg-red-100 text-red-800 border-red-200' },
  { id: 'high', name: 'High', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  { id: 'medium', name: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { id: 'low', name: 'Low', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { id: 'none', name: 'None', color: 'bg-gray-100 text-gray-800 border-gray-200' }
];

export interface DefaultPolicy {
  id: string;
  name: string;
  description: string;
  severity: string;
  rules: number;
}

export const DEFAULT_POLICIES: DefaultPolicy[] = [
  {
    id: 'standard',
    name: 'Standard License Check',
    description: 'Basic license compliance scanning with common license identification',
    severity: 'Medium',
    rules: 25
  },
  {
    id: 'enhanced',
    name: 'Enhanced Compliance',
    description: 'Comprehensive license scanning with copyleft risk assessment',
    severity: 'High',
    rules: 45
  },
  {
    id: 'enterprise',
    name: 'Enterprise Governance',
    description: 'Maximum license coverage with custom commercial license rules',
    severity: 'Critical',
    rules: 60
  },
  {
    id: 'compliance',
    name: 'Legal Compliance Plus',
    description: 'Industry-specific license compliance (Commercial, GPL, Proprietary)',
    severity: 'High',
    rules: 35
  }
];

export interface PolicyStandard {
  id: string;
  name: string;
  category: string;
}

export const POLICY_STANDARDS: PolicyStandard[] = [
  { id: 'gpl-2.0-only', name: 'GPL v2.0 Only', category: 'Critical Risk' },
  { id: 'gpl-2.0-or-later', name: 'GPL v2.0 or Later', category: 'Critical Risk' },
  { id: 'gpl-3.0-only', name: 'GPL v3.0 Only', category: 'Critical Risk' },
  { id: 'gpl-3.0-or-later', name: 'GPL v3.0 or Later', category: 'Critical Risk' },
  { id: 'agpl-3.0-only', name: 'AGPL v3.0 Only', category: 'Critical Risk' },
  { id: 'agpl-3.0-or-later', name: 'AGPL v3.0 or Later', category: 'Critical Risk' },
  { id: 'sspl-1.0', name: 'Server Side Public License v1.0', category: 'Critical Risk' },
  
  { id: 'lgpl-2.1-only', name: 'LGPL v2.1 Only', category: 'High Risk' },
  { id: 'lgpl-2.1-or-later', name: 'LGPL v2.1 or Later', category: 'High Risk' },
  { id: 'lgpl-3.0-only', name: 'LGPL v3.0 Only', category: 'High Risk' },
  { id: 'lgpl-3.0-or-later', name: 'LGPL v3.0 or Later', category: 'High Risk' },
  { id: 'mpl-2.0', name: 'Mozilla Public License 2.0', category: 'High Risk' },
  { id: 'epl-1.0', name: 'Eclipse Public License 1.0', category: 'High Risk' },
  { id: 'epl-2.0', name: 'Eclipse Public License 2.0', category: 'High Risk' },
  { id: 'cddl-1.0', name: 'CDDL v1.0', category: 'High Risk' },
  { id: 'cddl-1.1', name: 'CDDL v1.1', category: 'High Risk' },
  
  { id: 'mit', name: 'MIT License', category: 'Low Risk' },
  { id: 'apache-2.0', name: 'Apache License 2.0', category: 'Low Risk' },
  { id: 'bsd-2-clause', name: 'BSD 2-Clause License', category: 'Low Risk' },
  { id: 'bsd-3-clause', name: 'BSD 3-Clause License', category: 'Low Risk' },
  { id: 'isc', name: 'ISC License', category: 'Low Risk' },
  { id: 'unlicense', name: 'Unlicense', category: 'Low Risk' },
  { id: 'cc0-1.0', name: 'Creative Commons Zero v1.0', category: 'Low Risk' },
  { id: 'python-2.0', name: 'Python Software Foundation License 2.0', category: 'Low Risk' },
  { id: 'zlib', name: 'Zlib License', category: 'Low Risk' },
  { id: 'wtfpl', name: 'Do What The F*ck You Want To Public License', category: 'Low Risk' },
  { id: 'x11', name: 'X11 License', category: 'Low Risk' },
  { id: 'json', name: 'JSON License', category: 'Low Risk' },
  { id: 'openssl', name: 'OpenSSL License', category: 'Low Risk' },
  { id: 'boost-1.0', name: 'Boost Software License 1.0', category: 'Low Risk' },
  { id: 'bsl-1.0', name: 'Boost Software License 1.0', category: 'Low Risk' },
  { id: 'postgresql', name: 'PostgreSQL License', category: 'Low Risk' },
  
  { id: 'proprietary', name: 'Proprietary License', category: 'Commercial' },
  { id: 'commercial', name: 'Commercial License', category: 'Commercial' },
  { id: 'licenseref-proprietary', name: 'Custom Proprietary License', category: 'Commercial' },
  { id: 'custom-license', name: 'Custom License', category: 'Commercial' },
  
  { id: 'educational-community-v2.0', name: 'Educational Community License v2.0', category: 'Special' },
  { id: 'educational-community-v1.0', name: 'Educational Community License v1.0', category: 'Special' },
  { id: 'nasa-1.3', name: 'NASA Open Source Agreement 1.3', category: 'Special' },
  { id: 'us-government-work', name: 'US Government Work', category: 'Special' },
  { id: 'ogl-1.0', name: 'Open Government License v1.0', category: 'Special' },
  { id: 'ogl-2.0', name: 'Open Government License v2.0', category: 'Special' },
  { id: 'ogl-3.0', name: 'Open Government License v3.0', category: 'Special' },
  
  { id: 'noassertion', name: 'No License Assertion', category: 'Unknown Risk' },
  { id: 'licenseref-unknown', name: 'Unknown License Reference', category: 'Unknown Risk' },
  { id: 'unknown', name: 'Unknown License', category: 'Unknown Risk' },
  { id: 'dual-license', name: 'Dual Licensed', category: 'Unknown Risk' },
  { id: 'no-license', name: 'No License Found', category: 'Unknown Risk' }
];

export const TECHNOLOGY_STACKS: SelectOption[] = [
  { value: 'javascript', label: 'JavaScript/Node.js' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'dotnet', label: '.NET/C#' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'cpp', label: 'C/C++' },
  { value: 'swift', label: 'Swift' },
  { value: 'kotlin', label: 'Kotlin' },
  { value: 'scala', label: 'Scala' },
  { value: 'react', label: 'React' },
  { value: 'angular', label: 'Angular' },
  { value: 'vue', label: 'Vue.js' },
  { value: 'flutter', label: 'Flutter' },
  { value: 'react-native', label: 'React Native' },
  { value: 'other', label: 'Other' }
];

export const BUSINESS_IMPACT_OPTIONS: SelectOption[] = [
  { value: 'low', label: 'Low',score:1 },
  { value: 'medium', label: 'Medium',score:1 },
  { value: 'high', label: 'High',score:1 },
  { value: 'critical', label: 'Critical',score:1 }
];

export const APPLICATION_TYPE_OPTIONS: SelectOption[] = [
  { value: 'web', label: 'Web Application' },
  { value: 'mobile', label: 'Mobile Application' },
  { value: 'api', label: 'API/Web Service' },
  { value: 'desktop', label: 'Desktop Application' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'cloud', label: 'Cloud Service' },
  { value: 'embedded', label: 'Embedded System' },
  { value: 'other', label: 'Other' }
];

export const DEVELOPMENT_PHASE_OPTIONS: SelectOption[] = [
  { value: 'planning', label: 'Planning' },
  { value: 'development', label: 'Development' },
  { value: 'testing', label: 'Testing' },
  { value: 'staging', label: 'Staging' },
  { value: 'production', label: 'Production' },
  { value: 'maintenance', label: 'Maintenance' },
];

export const COUNTRIES: SelectOption[] = [
  { value: 'us', label: 'United States' },
  { value: 'ca', label: 'Canada' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'de', label: 'Germany' },
  { value: 'fr', label: 'France' },
  { value: 'it', label: 'Italy' },
  { value: 'es', label: 'Spain' },
  { value: 'nl', label: 'Netherlands' },
  { value: 'be', label: 'Belgium' },
  { value: 'ch', label: 'Switzerland' },
  { value: 'au', label: 'Australia' },
  { value: 'jp', label: 'Japan' },
  { value: 'kr', label: 'South Korea' },
  { value: 'cn', label: 'China' },
  { value: 'in', label: 'India' },
  { value: 'br', label: 'Brazil' },
  { value: 'mx', label: 'Mexico' },
  { value: 'sg', label: 'Singapore' },
  { value: 'hk', label: 'Hong Kong' },
  { value: 'nz', label: 'New Zealand' },
  { value: 'other', label: 'Other' }
];

export const COMPANY_TYPES: SelectOption[] = [
  { value: 'startup', label: 'Startup' },
  { value: 'small-business', label: 'Small Business' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'government', label: 'Government Agency' },
  { value: 'nonprofit', label: 'Non-profit Organisation' },
  { value: 'educational', label: 'Educational Institution' },
  { value: 'other', label: 'Other' }
];

export const CURRENCIES: SelectOption[] = [
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
  { value: 'CAD', label: 'Canadian Dollar (CAD)' },
  { value: 'AUD', label: 'Australian Dollar (AUD)' },
  { value: 'JPY', label: 'Japanese Yen (JPY)' },
  { value: 'CHF', label: 'Swiss Franc (CHF)' },
  { value: 'SEK', label: 'Swedish Krona (SEK)' },
  { value: 'NOK', label: 'Norwegian Krone (NOK)' },
  { value: 'DKK', label: 'Danish Krone (DKK)' }
];

export const TIMEZONES: SelectOption[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)' }
];

export const getBusinessImpactLabel = (value: string): string => {
  return BUSINESS_IMPACT_OPTIONS.find(impact => impact.value === value.toLowerCase())?.label || value;
}
export const getIndustryLabel = (value: string): string => {
  return INDUSTRIES.find(industry => industry.value === value.toLowerCase())?.label || value;
};

export const getCompanySizeLabel = (value: string): string => {
  return COMPANY_SIZES.find(size => size.value === value.toLowerCase())?.label || value;
};

export const getCountryLabel = (value: string): string => {
  return COUNTRIES.find(country => country.value === value.toLowerCase())?.label || value;
};

export const getCompanyTypeLabel = (value: string): string => {
  return COMPANY_TYPES.find(type => type.value === value.toLowerCase())?.label || value;
};

export const getCurrencyLabel = (value: string): string => {
  return CURRENCIES.find(currency => currency.value === value.toLowerCase())?.label || value;
};

export const getTimezoneLabel = (value: string): string => {
  return TIMEZONES.find(timezone => timezone.value === value.toLowerCase())?.label || value;
};

export const getApplicationTypeLabel = (value: string): string => {
  return APPLICATION_TYPE_OPTIONS.find(type => type.value === value.toLowerCase())?.label || value;
};

export const getDevelopmentPhaseLabel = (value: string): string => {
  return DEVELOPMENT_PHASE_OPTIONS.find(phase => phase.value === value.toLowerCase())?.label || value;
};

export const TEAM_ROLES: SelectOption[] = [
  { value: 'Security Lead', label: 'Security Lead' },
  { value: 'Security Manager', label: 'Security Manager' },
  { value: 'Penetration Tester', label: 'Penetration Tester' },
  { value: 'Security Analyst', label: 'Security Analyst' },
  { value: 'Junior Security Analyst', label: 'Junior Security Analyst' },
  { value: 'Security Architect', label: 'Security Architect' },
  { value: 'Compliance Officer', label: 'Compliance Officer' },
  { value: 'Risk Analyst', label: 'Risk Analyst' },
  { value: 'Security Engineer', label: 'Security Engineer' },
  { value: 'CISO', label: 'CISO' }
];

export const TEAM_DEPARTMENTS: SelectOption[] = [
  { value: 'Security Operations', label: 'Security Operations' },
  { value: 'Cybersecurity', label: 'Cybersecurity' },
  { value: 'Risk Management', label: 'Risk Management' },
  { value: 'Compliance', label: 'Compliance' },
  { value: 'Penetration Testing', label: 'Penetration Testing' },
  { value: 'Information Security', label: 'Information Security' },
  { value: 'IT Security', label: 'IT Security' },
  { value: 'Security Engineering', label: 'Security Engineering' }
];

export const getTeamRoleLabel = (value: string): string => {
  return TEAM_ROLES.find(role => role.value === value)?.label || value;
};

export const getTeamDepartmentLabel = (value: string): string => {
  return TEAM_DEPARTMENTS.find(dept => dept.value === value)?.label || value;
};

export const REPO_PROVIDERS: SelectOption[] = [
  { value: 'GitHub', label: 'GitHub' },
];

export const getRepoProviderLabel = (value: string): string => {
  return REPO_PROVIDERS.find(p => p.value === value)?.label || value;
};

export interface ORTConfiguration {
  scanType: string;
  enabledPackageManagers: string[];
  failOn: string;
  allowDynamicVersions: boolean;
  ortConfigRepository?: string;
  ortCliArgs?: string;
  ortCliAnalyzeArgs?: string;
  runStages: string[];
}

export const ORT_SCAN_TYPES: SelectOption[] = [
  { value: 'ort_license_scan', label: 'Standard Scan (License + Basic Security)' },
  { value: 'ort_full_scan', label: 'Full Scan (Complete Analysis)' }
];

export const ORT_PACKAGE_MANAGERS: SelectOption[] = [
  { value: 'NPM', label: 'NPM' },
  { value: 'Yarn', label: 'Yarn' },
  { value: 'Yarn2', label: 'Yarn 2+' },
  { value: 'Gradle', label: 'Gradle' },
  { value: 'Maven', label: 'Maven' },
  { value: 'SBT', label: 'SBT' },
  { value: 'PythonPip', label: 'Python Pip' },
  { value: 'PythonPoetry', label: 'Python Poetry' },
  { value: 'PythonPipenv', label: 'Python Pipenv' },
  { value: 'GoModules', label: 'Go Modules' },
  { value: 'NuGet', label: 'NuGet (.NET)' },
  { value: 'CocoaPods', label: 'CocoaPods' },
  { value: 'Carthage', label: 'Carthage' },
  { value: 'SwiftPM', label: 'Swift Package Manager' },
  { value: 'Composer', label: 'PHP Composer' },
  { value: 'Bundler', label: 'Ruby Bundler' },
  { value: 'Cargo', label: 'Rust Cargo' },
  { value: 'Pub', label: 'Dart Pub' },
  { value: 'Hackage', label: 'Haskell Hackage' },
  { value: 'DUB', label: 'D DUB' }
];

export const ORT_FAIL_ON_OPTIONS: SelectOption[] = [
  { value: 'violations', label: 'Policy Violations' },
  { value: 'all', label: 'All Issues' },
  { value: 'none', label: 'No Issues (Informational Only)' }
];

export const ORT_RUN_STAGES: SelectOption[] = [
  { value: 'cache-dependencies', label: 'Cache Dependencies' },
  { value: 'analyzer', label: 'Analyze Dependencies' },
  { value: 'advisor', label: 'Advise on Issues' },
  { value: 'reporter', label: 'Generate Reports' },
  { value: 'evaluator', label: 'Evaluate Policies' },
  { value: 'scanner', label: 'Scan for Vulnerabilities' }
];

export const ORT_REPORT_FORMATS: SelectOption[] = [
  { value: 'DynamicHtml', label: 'HTML Report' },
  { value: 'StaticHtml', label: 'Static HTML' },
  { value: 'CycloneDx', label: 'CycloneDX SBOM' },
  { value: 'SpdxDocument', label: 'SPDX Document' },
  { value: 'EvaluatedModel', label: 'Evaluated Model' },
  { value: 'WebAppNotifier', label: 'Web App Notification' }
];

export const ORT_DEFAULT_CONFIGS = {
  license_scan: {
    scanType: 'ort_license_scan',
    enabledPackageManagers: ['NPM', 'Yarn', 'Maven', 'Gradle', 'PythonPip'],
    failOn: 'violations',
    allowDynamicVersions: true,
    runStages: ['cache-dependencies', 'analyzer', 'advisor', 'reporter', 'evaluator']
  },
  vulnerability_scan: {
    scanType: 'ort_vulnerability_scan',
    enabledPackageManagers: ['NPM', 'Yarn', 'Maven', 'Gradle', 'PythonPip', 'NuGet'],
    failOn: 'violations',
    allowDynamicVersions: true,
    runStages: ['cache-dependencies', 'analyzer', 'advisor', 'scanner', 'reporter']
  },
  full_scan: {
    scanType: 'ort_full_scan',
    enabledPackageManagers: ['NPM', 'Yarn', 'Maven', 'Gradle', 'PythonPip', 'NuGet'],
    failOn: 'violations',
    allowDynamicVersions: true,
    runStages: ['cache-dependencies', 'analyzer', 'advisor', 'scanner', 'reporter', 'evaluator']
  }
};

export const getORTConfigTemplate = (scanType: string): ORTConfiguration => {
  switch (scanType) {
    case 'ort_license_scan':
      return ORT_DEFAULT_CONFIGS.license_scan;
    case 'ort_vulnerability_scan':
      return ORT_DEFAULT_CONFIGS.vulnerability_scan;
    case 'ort_full_scan':
      return ORT_DEFAULT_CONFIGS.full_scan;
    default:
      return ORT_DEFAULT_CONFIGS.full_scan;
  }
};
